import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/auth-context';
import { getTenantId, getApiUrl } from '../lib/config';

interface DashboardMetrics {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  profitMargin: number;
  activeCustomers: number;
  pendingInvoices: number;
  overdueInvoices: number;
}

interface DashboardChanges {
  revenue: number;
  expenses: number;
  profit: number;
}

interface RevenueSource {
  name: string;
  revenue: number;
  count: number;
  percentage: number;
  trend?: string;
}

interface RecentActivity {
  id: string;
  type: string;
  title: string;
  description: string;
  amount: number;
  date: string;
  status: string;
}

interface MonthlyTrend {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

interface ExpenseCategory {
  id: string;
  name: string;
  amount: number;
  color: string;
}

interface DashboardData {
  metrics: DashboardMetrics;
  changes: DashboardChanges;
  revenueSources: RevenueSource[];
  expenseCategories?: ExpenseCategory[];
  recentActivity: RecentActivity[];
  monthlyTrend: MonthlyTrend[];
  invoiceStatusBreakdown: Record<string, { count: number; amount: number }>;
  period: {
    days: number;
    startDate: string;
    endDate: string;
  };
  generatedAt: string;
}

interface UseDashboardDataReturn {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDashboardData(companyId?: string, period: number = 30): UseDashboardDataReturn {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchData = async () => {
    if (!companyId) {
      setLoading(true); // Keep loading true if no companyId to prevent showing zero values
      setError(null);
      setData(null); // Clear data if no companyId
      return;
    }

    setLoading(true);
    setError(null);
    // Don't clear data immediately - keep previous data while loading new data
    // setData(null); // Removed to prevent showing zero values during refresh

    try {
      const token = localStorage.getItem('auth_token');
      const tenantId = getTenantId();
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiUrl = `${getApiUrl()}/api/dashboard?companyId=${companyId}&period=${period}`;

      // Create abort controller for timeout (fallback for browsers that don't support AbortSignal.timeout)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      }).catch((fetchError) => {
        clearTimeout(timeoutId);
        // Catch network errors from fetch itself
        if (fetchError.name === 'AbortError' || fetchError.name === 'TimeoutError') {
          throw new Error('Request timeout - please check your internet connection');
        }
        if (fetchError.message?.includes('Failed to fetch') || 
            fetchError.message?.includes('NetworkError') ||
            fetchError.message?.includes('network')) {
          throw new Error('No internet connection. Please check your network and try again.');
        }
        throw fetchError;
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        } catch (parseError) {
          // If JSON parsing fails, it might be a network issue
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const dashboardData = await response.json();
      
      // Only set data if we got valid data
      if (dashboardData && dashboardData.metrics) {
        setData(dashboardData);
      } else {
        // If response is empty or invalid, keep previous data or set to null
        console.warn('Dashboard API returned invalid data:', dashboardData);
        setData(null);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      
      // Check if it's a network error
      const isNetworkError = err instanceof TypeError && (
        err.message.includes('Failed to fetch') ||
        err.message.includes('NetworkError') ||
        err.message.includes('network') ||
        err.message.includes('fetch')
      ) || (err instanceof Error && (
        err.message.includes('Failed to fetch') ||
        err.message.includes('NetworkError') ||
        err.message.includes('network')
      ));
      
      // If it's a network error and we have cached data, don't show error
      // Just keep the cached data visible
      if (isNetworkError && data) {
        console.log('Network error detected, but keeping cached data visible');
        setError(null); // Don't show error if we have cached data
      } else if (isNetworkError) {
        // Network error but no cached data - show user-friendly message
        setError('No internet connection. Please check your network and try again.');
      } else {
        // Other errors - show the actual error message
        setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
      }
      // Don't clear data on error - keep previous data visible
      // setData(null); // Removed to prevent showing zero values on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyId, period]);

  const refetch = () => {
    fetchData();
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
}
